import { useParams, Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, Check, CaretDown, CaretUp } from "@phosphor-icons/react";
import { getServiceBySlug, individualServices, businessServices } from "@/data/servicesData";
import { useState, useEffect } from "react";
import SEO, { serviceSchema, faqSchema, breadcrumbSchema } from "@/components/SEO";

const serviceKeywordMap = {
  "investing-trading": [
    "crypto investment advisory India",
    "bitcoin investment consultant",
    "crypto trading advisory",
    "crypto market analysis India",
    "portfolio strategy crypto",
  ],
  "financial-planning": [
    "crypto financial planning",
    "digital asset wealth planning",
    "crypto retirement planning",
    "crypto estate planning India",
    "NRI crypto financial planning",
  ],
  "tax-planning": [
    "crypto tax consultant India",
    "Section 115BBH tax planning",
    "Section 194S compliance",
    "crypto ITR filing India",
    "NRI crypto tax India",
  ],
  "portfolio-management": [
    "crypto portfolio management India",
    "digital asset portfolio advisor",
    "crypto portfolio rebalancing",
    "crypto performance reporting",
    "portfolio review crypto",
  ],
  "security-custody": [
    "crypto custody consulting",
    "hardware wallet setup India",
    "self custody crypto consultant",
    "multisig wallet advisory",
    "digital asset inheritance planning",
  ],
  "licensing-compliance": [
    "FIU-IND registration consultant",
    "crypto compliance India",
    "PMLA compliance crypto",
    "VDA service provider registration",
    "SEBI crypto advisory",
  ],
  "hiring-team-building": [
    "web3 hiring consultant",
    "blockchain talent advisory",
    "crypto recruitment strategy",
    "web3 team building",
    "hire blockchain developers India",
  ],
  "development": [
    "blockchain development consultancy",
    "smart contract development India",
    "DApp development company",
    "smart contract audit consultant",
    "web3 technical architecture",
  ],
  "project-management": [
    "crypto project management",
    "blockchain product delivery",
    "web3 program management",
    "token launch project management",
    "DAO project execution",
  ],
  "tokenization-launch": [
    "token launch advisory",
    "tokenomics consulting",
    "RWA tokenization consultant",
    "ICO IDO advisory",
    "whitepaper development crypto",
  ],
};

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[#383A46]/10">
      <button
        onClick={() => setOpen(!open)}
        className="w-full text-left py-5 flex items-center justify-between gap-4"
        data-testid={`faq-toggle-${q.slice(0, 20).replace(/\s/g, "-").toLowerCase()}`}
      >
        <span className="font-body font-medium text-base text-[#21324D]">{q}</span>
        {open ? <CaretUp size={16} className="text-[#A7A55C] flex-shrink-0" /> : <CaretDown size={16} className="text-[#383A46] flex-shrink-0" />}
      </button>
      {open && (
        <div className="pb-5 pr-8">
          <p className="font-body text-sm text-[#383A46] leading-relaxed">{a}</p>
        </div>
      )}
    </div>
  );
}

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = getServiceBySlug(slug);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F5F2]">
        <SEO
          title="Service Not Found"
          description="The BitGenix service you requested could not be found."
          path={`/services/${slug}`}
          robots="noindex, nofollow"
        />
        <div className="text-center">
          <h1 className="font-heading text-3xl text-[#21324D] mb-4">Service not found</h1>
          <Link to="/" className="font-body text-[#A7A55C] hover:underline">Return home</Link>
        </div>
      </div>
    );
  }

  const isIndividual = individualServices.some(s => s.slug === slug);
  const parentLink = isIndividual ? "/individuals" : "/businesses";
  const parentLabel = isIndividual ? "For Individuals" : "For Businesses";
  const Icon = service.icon;
  const peerServices = (isIndividual ? individualServices : businessServices)
    .filter((item) => item.slug !== slug)
    .slice(0, 3);

  return (
    <div data-testid={`service-page-${slug}`}>
      <SEO
        title={`${service.title} | Crypto Consultancy India`}
        description={service.heroDesc}
        path={`/services/${slug}`}
        keywords={serviceKeywordMap[slug]}
        jsonLd={[
          serviceSchema(service, isIndividual),
          faqSchema(service.faq),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: parentLabel, path: parentLink },
            { name: service.title, path: `/services/${slug}` },
          ]),
        ]}
      />
      {/* Hero */}
      <section className="bg-[#21324D] py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <Link
            to={parentLink}
            className="font-body text-sm text-[#F5F5F2]/50 hover:text-[#A7A55C] transition-colors inline-flex items-center gap-2 mb-8"
            data-testid="service-back-link"
          >
            <ArrowLeft size={14} /> {parentLabel}
          </Link>
          <div className="flex items-start gap-6 mb-8">
            <Icon size={40} weight="duotone" className="text-[#A7A55C] mt-1 hidden sm:block" />
            <div>
              <h1
                className="font-heading font-semibold text-4xl sm:text-5xl text-[#F5F5F2] leading-none tracking-tight mb-6"
                data-testid="service-detail-heading"
              >
                {service.title}
              </h1>
              <p className="font-body text-lg text-[#F5F5F2]/80 leading-relaxed max-w-2xl">
                {service.heroDesc}
              </p>
            </div>
          </div>
          <Link
            to="/contact"
            className="bg-[#A7A55C] text-[#F5F5F2] hover:bg-[#8C8A4A] transition-colors duration-300 font-body font-semibold text-sm tracking-wide px-8 py-4 rounded-none inline-flex items-center gap-2"
            data-testid="service-hero-cta"
          >
            Get Started <ArrowRight size={16} weight="bold" />
          </Link>
        </div>
      </section>

      {/* Problem Statement */}
      <section className="py-24 sm:py-32 bg-[#F5F5F2]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
            <div className="lg:col-span-5">
              <p className="font-body text-sm uppercase tracking-[0.15em] text-[#A7A55C] font-medium mb-4">The Problem</p>
              <h2 className="font-heading font-semibold text-3xl sm:text-4xl text-[#21324D] tracking-tight leading-tight">
                Why this matters
              </h2>
            </div>
            <div className="lg:col-span-7">
              <p className="font-body text-base text-[#383A46] leading-relaxed">
                {service.problem}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-24 sm:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
            <div className="lg:col-span-5">
              <p className="font-body text-sm uppercase tracking-[0.15em] text-[#A7A55C] font-medium mb-4">Our Approach</p>
              <h2 className="font-heading font-semibold text-3xl sm:text-4xl text-[#21324D] tracking-tight leading-tight">
                How we help
              </h2>
            </div>
            <div className="lg:col-span-7">
              <p className="font-body text-base text-[#383A46] leading-relaxed">
                {service.approach}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-24 sm:py-32 bg-[#F5F5F2]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <p className="font-body text-sm uppercase tracking-[0.15em] text-[#A7A55C] font-medium mb-4">Deliverables</p>
          <h2 className="font-heading font-semibold text-3xl sm:text-4xl text-[#21324D] tracking-tight leading-tight mb-12">
            What's included
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {service.included.map((item, i) => (
              <div key={i} className="flex items-start gap-4 bg-white border border-[#383A46]/10 p-6 hover:border-[#A7A55C]/30 transition-colors duration-300">
                <div className="bg-[#21324D] w-7 h-7 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check size={14} weight="bold" className="text-[#A7A55C]" />
                </div>
                <p className="font-body text-sm text-[#383A46] leading-relaxed">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who It's For */}
      <section className="py-24 sm:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <p className="font-body text-sm uppercase tracking-[0.15em] text-[#A7A55C] font-medium mb-4">Ideal For</p>
          <h2 className="font-heading font-semibold text-3xl sm:text-4xl text-[#21324D] tracking-tight leading-tight mb-12">
            Who this is for
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {service.whoFor.map((item, i) => (
              <div key={i} className="flex items-start gap-4">
                <span className="w-2 h-2 rounded-full bg-[#A7A55C] mt-2 flex-shrink-0" />
                <p className="font-body text-base text-[#383A46]">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 sm:py-32 bg-[#F5F5F2]" data-testid="service-faq-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="max-w-3xl">
            <p className="font-body text-sm uppercase tracking-[0.15em] text-[#A7A55C] font-medium mb-4">FAQ</p>
            <h2 className="font-heading font-semibold text-3xl sm:text-4xl text-[#21324D] tracking-tight leading-tight mb-12">
              Common questions
            </h2>
            <div>
              {service.faq.map((item, i) => (
                <FAQItem key={i} q={item.q} a={item.a} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {peerServices.length > 0 && (
        <section className="py-20 sm:py-24 bg-white" data-testid="related-services-section">
          <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-10">
              <div>
                <p className="font-body text-sm uppercase tracking-[0.15em] text-[#A7A55C] font-medium mb-4">Related Services</p>
                <h2 className="font-heading font-semibold text-3xl sm:text-4xl text-[#21324D] tracking-tight leading-tight">
                  Explore more ways we can help
                </h2>
              </div>
              <Link
                to={parentLink}
                className="font-body font-semibold text-sm text-[#A7A55C] inline-flex items-center gap-2"
              >
                View all {isIndividual ? "individual" : "business"} services <ArrowRight size={14} weight="bold" />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {peerServices.map((item) => (
                <Link
                  key={item.slug}
                  to={`/services/${item.slug}`}
                  className="group border border-[#383A46]/10 p-8 hover:border-[#A7A55C]/40 transition-all duration-300"
                  data-testid={`related-service-${item.slug}`}
                >
                  <div className="bg-[#21324D] w-12 h-12 flex items-center justify-center mb-6">
                    <item.icon size={20} weight="duotone" className="text-[#A7A55C]" />
                  </div>
                  <h3 className="font-body font-medium text-lg text-[#21324D] mb-3 group-hover:text-[#A7A55C] transition-colors">
                    {item.title}
                  </h3>
                  <p className="font-body text-sm text-[#383A46]/70 leading-relaxed mb-5">
                    {item.shortDesc}
                  </p>
                  <span className="font-body font-semibold text-sm text-[#A7A55C] inline-flex items-center gap-2">
                    Learn more <ArrowRight size={14} weight="bold" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-24 sm:py-32 bg-[#21324D]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <h2 className="font-heading font-semibold text-3xl sm:text-4xl text-[#F5F5F2] tracking-tight leading-tight mb-6">
            Ready to get started?
          </h2>
          <p className="font-body text-base text-[#F5F5F2]/70 max-w-xl mx-auto mb-10">
            Book a free consultation to discuss your needs. No obligations.
          </p>
          <Link
            to="/contact"
            className="bg-[#A7A55C] text-[#F5F5F2] hover:bg-[#8C8A4A] transition-colors duration-300 font-body font-semibold text-sm tracking-wide px-8 py-4 rounded-none inline-flex items-center gap-2"
            data-testid="service-cta-button"
          >
            Book a Consultation <ArrowRight size={16} weight="bold" />
          </Link>
        </div>
      </section>
    </div>
  );
}
